import { defineStore } from "pinia";

export const useInactivityStore = defineStore("inactivity", {
	state: () => ({
		// 初期値（本番は 60 分）
		limitMs: 60 * 60 * 1000,
		events: ["mousemove", "keydown", "click", "touchstart"],
		timer: null, // setTimeout 用（満了時に logoutFn を叩く）
		tickId: null, // setInterval 用（残り時間ログ出力）
		attached: false,
		logoutFn: null,
		handler: null,
		deadlineAt: null, // Date.now() + limitMs
		debug: false, // true ならコンソールに残り時間を出す
	}),
	getters: {
		getRemainingMs: (state) => {
			if (!state.deadlineAt) return null;
			return Math.max(0, state.deadlineAt - Date.now());
		},
		getElapsedMs: (state) => {
			if (!state.deadlineAt) return null;
			const startAt = state.deadlineAt - state.limitMs;
			return Math.max(0, Date.now() - startAt);
		},
	},
	actions: {
		setLogoutFn(fn) {
			this.logoutFn = fn;
		},
		configure({ limitMs, events, debug } = {}) {
			if (typeof limitMs === "number") this.limitMs = limitMs;
			if (Array.isArray(events)) this.events = events;
			if (typeof debug === "boolean") this.debug = debug;
		},
		_startTimer() {
			// 満了時刻を更新
			this.deadlineAt = Date.now() + this.limitMs;
			// 既存タイマーをクリア
			if (this.timer) clearTimeout(this.timer);
			// 無操作満了でログアウト実行
			this.timer = setTimeout(async () => {
				this._stopTick(); // ログは止める
				try {
					if (this.debug) console.log("[Inactivity] timeout -> call logoutFn()");
					await this.logoutFn?.();
				} catch (e) {
					console.warn("logout failed in inactivity store:", e);
				}
			}, this.limitMs);
			// デバッグ表示
			if (this.debug) {
				console.log(`[Inactivity] start/reset: limit=${this.limitMs}ms, deadline=${new Date(this.deadlineAt).toLocaleTimeString()}`);
			}
			this._startTick(); // 残り時間の定期ログ
		},
		_startTick() {
			if (!this.debug) return;
			// 既存Intervalがあればクリア
			if (this.tickId) clearInterval(this.tickId);
			// 1秒ごとに残り時間を表示
			this.tickId = setInterval(() => {
				if (!this.deadlineAt) return;
				const remaining = this.deadlineAt - Date.now();
				if (remaining <= 0) {
					console.log("[Inactivity] remaining: 00:00");
					return;
				}
				console.log(`[Inactivity] remaining: ${formatMs(remaining)}`);
			}, 1000);
		},
		_stopTick() {
			if (this.tickId) {
				clearInterval(this.tickId);
				this.tickId = null;
			}
		},
		start() {
			if (!this.logoutFn) {
				console.warn("logoutFn is not set");
				return;
			}
			this._startTimer();
		},
		reset() {
			this._startTimer();
		},
		attach() {
			if (this.attached) return;
			if (!this.handler) this.handler = () => this.reset();
			this.events.forEach((evt) => window.addEventListener(evt, this.handler, { passive: true }));
			this.attached = true;
			if (this.debug) console.log("[Inactivity] listeners attached");
		},
		detach() {
			if (!this.attached) return;
			this.events.forEach((evt) => window.removeEventListener(evt, this.handler));
			this.attached = false;
			this.pause();
			if (this.debug) console.log("[Inactivity] listeners detached");
		},
		pause() {
			if (this.timer) {
				clearTimeout(this.timer);
				this.timer = null;
			}
			this._stopTick();
			this.deadlineAt = null;
			if (this.debug) console.log("[Inactivity] paused");
		},
	},
});
/** mm:ss 形式に整形 */
function formatMs(ms) {
	const totalSec = Math.max(0, Math.floor(ms / 1000));
	const m = Math.floor(totalSec / 60);
	const s = totalSec % 60;
	return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
